import { FC, useEffect, useRef, useState } from 'react';
import { ConnectionIndicator } from '../../components/widgets/Connection/connectionIndicator';
import { getBleDeviceCandidates, getBleService, getMisoCharacteristic, getMosiCharacteristic, getPumpCharacteristic, getBuzzerCharacteristic, navigatorSupportsWebBle, writeToCharacteristic } from '../../services/ble/ble';
import { DEFAULT_DEVICE_DETECTION_INTERVAL } from '../../components/serial/constants';
import {
  ARYIOT_CUT_MSG_EVENT_STATE,
  ARYIOT_EVENT_TYPE,
  checkBuzzerCommand,
  CSM_PROTOCOL_COMMAND_TYPE,
  CSM_PROTOCOL_EVENT_TYPE,
  CSM_PROTOCOL_LIFECYCLE_EVENT_STATE,
  decodeAryIOTMsgCutEvent,
  decodeBatteryInfo,
  decodeFloatsFromHIHbuffer,
  decodeFlowrateValue,
  decodeMisoFrame,
  decodePumpPowerResponse,
  encodeMosiCommand,
  encodePumpPowerCommandLegacy,
  parseBiosensorSignalMapResponse,
  parseEventPayload,
  parseResponsePayload,
  parseSerialNumberResponse,
  parseTextResponse,
  parseVersionsResponse,
  PERIPHERAL_ADDRESS,
  trimTrailingZeros,
  VERSION_INFO_ENTRIES,
} from '../../components/serial/csm';
import { v4 as uuidv4 } from 'uuid';
import { saveSpotsGrid1D } from '../../services/cache/localStorage';
import { Mutex, withTimeout, MutexInterface } from 'async-mutex';
import { message as antdMessage } from 'antd';
import { useMessageContext } from '../context/MessageContext';
import { CSMCommandMessage } from '../../types/types';

export const CSMBleMessageDispatcher: FC<{
  minimized: boolean;
}> = ({ minimized }) => {
  // Important! Two notification characteristics on a single device causes unknown GATTT error on Androind
  const [misoCharacteristic, setMisoCharacteristic] = useState<BluetoothRemoteGATTCharacteristic | null>(null);
  const [mosiCharacteristic, setMosiCharacteristic] = useState<BluetoothRemoteGATTCharacteristic | null>(null);
  const [pumpCharacteristic, setPumpCharacteristic] = useState<BluetoothRemoteGATTCharacteristic | null>(null);
  const [buzzerCharacteristic, setBuzzerCharacteristic] = useState<BluetoothRemoteGATTCharacteristic | null>(null);

  const sendCommandMutexRef = useRef<MutexInterface>(withTimeout(new Mutex(), 1000));
  const lastProcessedCommandRef = useRef<string>(''); // to remember last processed command to avoid processing it again while it is cleared out from the csmCommands buffer

  // for init
  // first command is a stop sampling during init (which generates a "CSM is ready" message)
  // We'd like to discard it to avoid any confusion as the init is not finished at that time
  const discardReadyAcquiringEvent = useRef<boolean>(true);

  // for split messages
  const receivingMessagePackets = useRef<boolean>(false);
  const receivedMsgId = useRef<number>(-1);
  const frameArrayRef = useRef<Uint8Array>(new Uint8Array());
  const nbPacketsReceived = useRef<number>(0);

  const {
    csmMessages,
    csmCommands,
    csmBleDevice,
    csmIsConnected,
    csmState,
    aryiotFwVersion,
    addCSMMessage,
    addCSMCommand,
    setCSMBleDevice,
    setCSMIsConnected,
    setAryiotFwVersion,
    setCSMFwVersion,
    setCSMstate,
    resetCSMstate,
    consumeCSMCommand,
    clearCSMCommands,
    saveHihValue,
    setCSMSerialNumber,
    setBatteryInfo,
    setInitialMmiPartitionKey,
  } = useMessageContext();

  const resetCutMsgRefs = () => {
    receivingMessagePackets.current = false;
    receivedMsgId.current = -1;
    frameArrayRef.current = new Uint8Array();
    nbPacketsReceived.current = 0;
  };

  const handleFragmentedFrames = (data: Uint8Array): Uint8Array | null => {
    // prepare/stop receiving big messages split into several packets by AryIOT
    if (data[0] === PERIPHERAL_ADDRESS.ARYIOT && data[1] === ARYIOT_EVENT_TYPE.CutMsg) {
      // console.log('receiving cut msg event', data);
      try {
        let decodedEvent = decodeAryIOTMsgCutEvent(data);
        if (decodedEvent.State === ARYIOT_CUT_MSG_EVENT_STATE.Sending) {
          receivingMessagePackets.current = true;
          receivedMsgId.current = decodedEvent.MsgId;
          // console.log('start collecting message with ID %d of length %d split in %d packets', decodedEvent.MsgId, decodedEvent.MsgLength, decodedEvent.NbPackets);
          return null;
        } else {
          // sanity checks
          if (receivedMsgId.current !== decodedEvent.MsgId) {
            console.error('error validating packets collection, msgId mismatch: was collecting id %d and received transfer complete event for id %d', receivedMsgId.current, decodedEvent.MsgId);
            resetCutMsgRefs();
            return null;
          }
          if (nbPacketsReceived.current !== decodedEvent.NbPackets) {
            console.error('error validating packets collection, mismatch in nb of packets: expected %d packets, received only %d', decodedEvent.NbPackets, nbPacketsReceived.current);
            resetCutMsgRefs();
            return null;
          }

          // message has been  successfully received, and is safe to dispatch
          // reset variables and continue with the rest of the processing
          // console.debug('packets collection complete for message %d', decodedEvent.MsgId);
          let completedFrame = frameArrayRef.current;
          resetCutMsgRefs();
          return completedFrame;
        }
      } catch {
        console.error('Could not parse cut message event. Aborting trying to reconstitute CSM message');
        resetCutMsgRefs();
        return null;
      }
    }

    // accumulate packets received from Aryiot
    if (receivingMessagePackets.current) {
      // console.debug('receiving packets...', data);
      var mergedArray = new Uint8Array([...frameArrayRef.current, ...data]);
      frameArrayRef.current = mergedArray;
      nbPacketsReceived.current++;
      return null;
    }

    // Normal non-fragmented frame
    return data;
  };

  const handleAryiotFrameReceived = (data: Uint8Array) => {
    switch (data[1]) {
      case CSM_PROTOCOL_COMMAND_TYPE.GetAryIOTVersion:
      case CSM_PROTOCOL_COMMAND_TYPE.GetAryIOTname:
        let frame = decodeMisoFrame(data);
        if (frame.LocalCRC !== frame.RemoteCRC) {
          return;
        }
        addCSMMessage({
          id: uuidv4().toString(),
          ts: Date.now(),
          message: frame,
        });

        if (frame.Type === CSM_PROTOCOL_COMMAND_TYPE.GetAryIOTVersion) {
          const parsedMsg = parseTextResponse(trimTrailingZeros(frame.Payload));
          console.log('Decoded aryiot fwVersion', parsedMsg);
          setAryiotFwVersion(parsedMsg);
        }
    }
    return;
  };

  const handleHihFrameReceived = (data: Uint8Array) => {
    if (data.length > 9) {
      // new convention
      let frame = decodeMisoFrame(data);
      if (frame.LocalCRC !== frame.RemoteCRC) {
        return;
      }
      const [temperature, humidity] = decodeFloatsFromHIHbuffer(frame.Payload.buffer, 0);
      saveHihValue(humidity, temperature);
    } else {
      // old convention encoded as buffer with
      // 1 byte : peripheral (0x02)
      // 4 bytes : temperature
      // 4 bytes : humidity
      const [temperature, humidity] = decodeFloatsFromHIHbuffer(data.buffer, 1);
      saveHihValue(humidity, temperature);
    }
    return;
  };

  const handleBatteryFrameReceived = (data: Uint8Array) => {
    console.log('battery miso response is', data);
    let frame = decodeMisoFrame(data);
    if (frame.LocalCRC !== frame.RemoteCRC) {
      return;
    }
    let batteryInfo = decodeBatteryInfo(frame.Payload);
    console.log('battery info are ', batteryInfo);
    setBatteryInfo(batteryInfo);
    return;
  };

  const handlePumpFrameReceived = (data: Uint8Array) => {
    if (pumpCharacteristic === null) {
      // new FW convention : no characteristic & pump message following CSM MISO conventions
      let frame = decodeMisoFrame(data);
      if (frame.LocalCRC !== frame.RemoteCRC) {
        return;
      }

      addCSMMessage({
        id: uuidv4().toString(),
        ts: Date.now(),
        message: frame,
      });
    } else {
      // old FW convention: fake MISO without CRC or stuffing
      const frame = decodePumpPowerResponse(data);
      addCSMMessage({
        id: uuidv4().toString(),
        ts: Date.now(),
        message: frame,
      });
    }
    return;
  };

  const handleFlowmeterFrameReceived = (data: Uint8Array) => {
    let frame = decodeMisoFrame(data);
    if (frame.LocalCRC !== frame.RemoteCRC) {
      return;
    }
    let flowrate = decodeFlowrateValue(frame.Payload);
    antdMessage.info(`received flowrate value ${flowrate}`);
    return;
  };

  const handleCsmFrameReceived = (data: Uint8Array) => {
    let frame = decodeMisoFrame(data);
    if (frame.LocalCRC !== frame.RemoteCRC) {
      return;
    }

    if (frame.Type < 0x80) {
      // miso responses
      console.log('miso response', frame);
      addCSMMessage({
        id: uuidv4().toString(),
        ts: Date.now(),
        message: frame,
      });
      switch (frame.Type) {
        case CSM_PROTOCOL_COMMAND_TYPE.GetBiosensorSignalMap:
          console.log('got biosensor signal map', frame.Payload);
          try {
            console.log(frame.Payload);
            let responsePayload = parseResponsePayload(frame.Payload);
            console.log('responsePayload', responsePayload);
            let spotsgrid1d = parseBiosensorSignalMapResponse(responsePayload.Data);
            //spotsgrid1d.shift();
            // [66, 63, 25, 1, 64, 27, 19, 24, 29, 26, 18, 28, 20, 21, 62, 30, 23, 55, 54, 22, 65]
            console.log('biosensor signal map', spotsgrid1d);
            saveSpotsGrid1D(spotsgrid1d);
          } catch (e: any) {
            console.log('error parsing biosensor signal map response', e.message);
          }
          break;
        case CSM_PROTOCOL_COMMAND_TYPE.GetBiosensorSerialNumber:
          const serialNumber = parseSerialNumberResponse(frame.Payload);
          console.log(serialNumber);
          setCSMSerialNumber(serialNumber);
          break;
        case CSM_PROTOCOL_COMMAND_TYPE.GetVersions:
          let versionInfo = parseVersionsResponse(frame.Payload.slice(1));
          setCSMFwVersion(versionInfo[VERSION_INFO_ENTRIES.fwVersion]);
          break;
      }
    } else {
      //console.log('frame type is:', frame.Type)
      // miso events
      switch (frame.Type) {
        case CSM_PROTOCOL_EVENT_TYPE.BiosensorsSignalEvent:
        case CSM_PROTOCOL_EVENT_TYPE.ImageEvent:
        case CSM_PROTOCOL_EVENT_TYPE.MmiEvent:
          addCSMMessage({
            id: uuidv4().toString(),
            ts: Date.now(),
            message: frame,
          });
          break;
        case CSM_PROTOCOL_EVENT_TYPE.ErrorEvent:
          console.log('csm protocol error: ', frame.Payload);
          let errorEvent = parseEventPayload(frame.Payload);
          let errorEventString = new TextDecoder().decode(errorEvent.Data);
          antdMessage.error(`Error from device: ${errorEventString}`);
          console.log(`Error from device: ${errorEventString}`);
          break;
        case CSM_PROTOCOL_EVENT_TYPE.HeartbeatEvent:
          let event = parseEventPayload(frame.Payload);
          // console.log('csm protocol heartbeat: ', event.Counter)
          break;
        case CSM_PROTOCOL_EVENT_TYPE.LifeCycleEvent:
          addCSMMessage({
            id: uuidv4().toString(),
            ts: Date.now(),
            message: frame,
          });
          let lcEvent = parseEventPayload(frame.Payload);
          let lcState = lcEvent.Data[0] as CSM_PROTOCOL_LIFECYCLE_EVENT_STATE;
          let lcDataByte = lcEvent.Data[0];
          if (lcDataByte === undefined) {
            break;
          }
          let lcStateString = CSM_PROTOCOL_LIFECYCLE_EVENT_STATE[lcDataByte];
          console.log('csm protocol lifecycle: ', lcEvent, lcStateString);
          switch (lcState) {
            case CSM_PROTOCOL_LIFECYCLE_EVENT_STATE.Initializing:
            case CSM_PROTOCOL_LIFECYCLE_EVENT_STATE.Calibrating:
              discardReadyAcquiringEvent.current = false;
              setCSMstate(lcState);
              antdMessage.info(`NeOse is ${lcStateString}..`);
              break;
            case CSM_PROTOCOL_LIFECYCLE_EVENT_STATE.Ready:
            case CSM_PROTOCOL_LIFECYCLE_EVENT_STATE.Acquiring:
              if (!discardReadyAcquiringEvent.current) {
                setCSMstate(lcState);
                antdMessage.success(`NeOse is ${lcStateString}!`);
              }
              break;
            case CSM_PROTOCOL_LIFECYCLE_EVENT_STATE.CalibrationError:
            case CSM_PROTOCOL_LIFECYCLE_EVENT_STATE.InitializationError:
              setCSMstate(lcState);
              antdMessage.error(`NeOse LifeCycle ${lcStateString} 😿! You might need to unplug and replug the sensor..`);
              break;
          }
          break;
        default:
          console.log('csm protocol unknown: ', frame.Type, frame.Payload);
      }
    }
  };

  const handleMisoCharacteristicValueChanged = (event: Event) => {
    if (event.target === null) {
      return;
    }
    let v = (event.target as BluetoothRemoteGATTCharacteristic).value;
    if (!v) {
      return;
    }

    let frameArray = new Uint8Array(v.buffer);
    if (frameArray.length < 2) {
      console.log('received array of size ', frameArray.length, frameArray);
      return;
    }
    // console.log(frameArray.byteLength)

    const fullFrame = handleFragmentedFrames(frameArray);
    if (!fullFrame) return;

    switch (fullFrame[0]) {
      case PERIPHERAL_ADDRESS.HIH:
        handleHihFrameReceived(fullFrame);
        return;
      case PERIPHERAL_ADDRESS.BATTERY:
        handleBatteryFrameReceived(fullFrame);
        return;
      case PERIPHERAL_ADDRESS.PUMP:
        handlePumpFrameReceived(fullFrame);
        return;
      case PERIPHERAL_ADDRESS.ARYIOT:
        handleAryiotFrameReceived(fullFrame);
        return;
      case PERIPHERAL_ADDRESS.FLOWMETER:
        handleFlowmeterFrameReceived(fullFrame);
        return;
      case PERIPHERAL_ADDRESS.CSM:
        handleCsmFrameReceived(fullFrame);
        return;
      default:
        console.log('unknown perpheral type ', fullFrame[0]);
        return;
    }
    // console.log('miso characteristic value', v, frame, event.timeStamp)
  };

  const writeAndProcessBleCommand = (characteristic: BluetoothRemoteGATTCharacteristic, payload: Uint8Array, cmdId: string, release: MutexInterface.Releaser) => {
    setTimeout(() => {
      // console.log('writing to characteristic', payload)
      writeToCharacteristic(characteristic, payload).finally(() => {
        lastProcessedCommandRef.current = cmdId;
        consumeCSMCommand(cmdId);
        console.log('releasing command mutex');
        release();
      });
    }, 200);
  };

  const discardBleCommand = (cmdId: string, release: MutexInterface.Releaser) => {
    console.log(`Unsupported command with this "${aryiotFwVersion}" aryiot fw version. Discarded.`);
    lastProcessedCommandRef.current = cmdId;
    consumeCSMCommand(cmdId);
    console.log('releasing command mutex');
    release();
  };

  // effect on miso characteristic
  useEffect(() => {
    if (misoCharacteristic === null) {
      return;
    }
    try {
      misoCharacteristic.oncharacteristicvaluechanged = handleMisoCharacteristicValueChanged;
      misoCharacteristic.startNotifications();
      console.log('miso characteristic with value changed handler', misoCharacteristic);
    } catch (e: any) {
      console.log('error starting notifications for miso characteristic', e.message);
    }
  }, [misoCharacteristic]);

  // send all init commands at initialisation
  useEffect(() => {
    if (mosiCharacteristic === null) {
      return;
    }
    clearCSMCommands();

    // reset fw versions
    setAryiotFwVersion('');
    setCSMFwVersion('');
    setInitialMmiPartitionKey('');

    // sends first a stop sampling to make sure the next commands/responses are sent/received
    discardReadyAcquiringEvent.current = true;
    addCSMCommand({
      id: uuidv4().toString(),
      message: {
        CmdType: CSM_PROTOCOL_COMMAND_TYPE.StopSampling,
      },
    });

    // here position matters, issued right after a stop sampling
    // because old aryiot will not handle the getAryiotFwVersion on its side and will foward it to tboard
    // and when a tboard receives an unknown command, it replies with a miso response with :
    //   - the command type of the previous command received by the tboard
    //   - a 1-byte payload containing just a "0" status
    // receiving such a "stop sampling" response is fine as the response for that command is unused
    addCSMCommand({
      id: uuidv4().toString(),
      message: {
        CmdType: CSM_PROTOCOL_COMMAND_TYPE.GetAryIOTVersion,
        PeripheralType: PERIPHERAL_ADDRESS.ARYIOT,
      },
    });

    addCSMCommand({
      id: uuidv4().toString(),
      message: {
        CmdType: CSM_PROTOCOL_COMMAND_TYPE.AccessoryGetCmd,
        PeripheralType: PERIPHERAL_ADDRESS.BATTERY,
      },
    });

    addCSMCommand({
      id: uuidv4().toString(),
      message: {
        CmdType: CSM_PROTOCOL_COMMAND_TYPE.GetBiosensorSignalMap,
      },
    });
    addCSMCommand({
      id: uuidv4().toString(),
      message: {
        CmdType: CSM_PROTOCOL_COMMAND_TYPE.GetBiosensorSerialNumber,
      },
    });
    addCSMCommand({
      id: uuidv4().toString(),
      message: {
        CmdType: CSM_PROTOCOL_COMMAND_TYPE.GetVersions,
      },
    });
    resetCSMstate();
    addCSMCommand({
      id: uuidv4().toString(),
      message: {
        CmdType: CSM_PROTOCOL_COMMAND_TYPE.DeviceReset,
      },
    });
  }, [mosiCharacteristic]);

  // effect on mosi characteristic changed
  useEffect(() => {
    if (csmCommands.length === 0) {
      console.log('csm ble dispatcher: no commands to send');
      return;
    }
    if (csmBleDevice?.gatt?.connected === false) {
      console.log('csm ble dispatcher: device is not connected. will not send commands');
      return;
    }
    if (mosiCharacteristic === null) {
      console.log('csm ble dispatcher: mosi characteristic is null. will not send commands');
      return;
    }
    let t = Date.now();
    console.log('acquiring mutex to send command..');
    sendCommandMutexRef.current.acquire().then((release) => {
      console.log('command mutex acquired after ', Date.now() - t, 'ms');
      console.log('csm command buffer is ', csmCommands);
      let cmd = csmCommands[0];
      if (lastProcessedCommandRef.current === cmd.id) {
        console.log('skipped already processed command', cmd.id);
        release();
        return;
      }
      console.log('csm ble dispatcher: sending command', cmd.message.CmdType, 'for peripheral', cmd.message.PeripheralType);
      try {
        if (aryiotFwVersion !== '') {
          // aryiot fw is able to handle accessories commands in addition to CSM ones through MOSI characteristic
          // so send all messages (csm + aryiot + accessories) to MOSI characteristic
          let frame = encodeMosiCommand(cmd.message.CmdType, cmd.message.Payload, cmd.message.PeripheralType);
          writeAndProcessBleCommand(mosiCharacteristic, frame, cmd.id, release);
        } else {
          // either aryiot fw version hasn't been retrieved yet
          // or this is a legacy fw that can't return its version and process accessories commands on their own characteristic
          if (mosiCharacteristic !== null && cmd.message.PeripheralType === PERIPHERAL_ADDRESS.ARYIOT && cmd.message.CmdType === CSM_PROTOCOL_COMMAND_TYPE.GetAryIOTVersion) {
            // if version hasn't been retrieved yet, we need to allow this command to be sent to MOSI even if legacy firmware is not able to process it
            let frame = encodeMosiCommand(cmd.message.CmdType, cmd.message.Payload, cmd.message.PeripheralType);
            writeAndProcessBleCommand(mosiCharacteristic, frame, cmd.id, release);
          } else if (pumpCharacteristic !== null && cmd.message.PeripheralType === PERIPHERAL_ADDRESS.PUMP) {
            // legacy aryiot fw: process old pump commands through dedicated pump characteristic
            let payload = encodePumpPowerCommandLegacy(cmd.message.CmdType, cmd.message.Payload);
            writeAndProcessBleCommand(pumpCharacteristic, payload, cmd.id, release);
          } else if (buzzerCharacteristic !== null && cmd.message.PeripheralType === PERIPHERAL_ADDRESS.BUZZER) {
            // legacy aryiot fw: process old buzzer commands through dedicated buzzer characteristic
            let payload = checkBuzzerCommand(cmd.message.Payload);
            writeAndProcessBleCommand(buzzerCharacteristic, payload, cmd.id, release);
          } else if (mosiCharacteristic !== null && (cmd.message.PeripheralType === PERIPHERAL_ADDRESS.CSM || cmd.message.PeripheralType === undefined)) {
            // legacy aryiot fw: process rest of commands starting with CSM header through MOSI characteristic
            let frame = encodeMosiCommand(cmd.message.CmdType, cmd.message.Payload, cmd.message.PeripheralType);
            writeAndProcessBleCommand(mosiCharacteristic, frame, cmd.id, release);
          } else {
            // legacy aryiot fw: discard all remaining commands as they are not compatible with these legacy FW versions
            discardBleCommand(cmd.id, release);
          }
        }
      } catch (e: any) {
        console.log('error writing to mosi characteristic', e.message);
      }
    });
  }, [csmCommands, mosiCharacteristic, pumpCharacteristic, buzzerCharacteristic, csmBleDevice, aryiotFwVersion]);

  useEffect(() => {
    if (csmBleDevice === undefined) {
      return;
    }
    getBleService(csmBleDevice)
      .then((service) => {
        getMisoCharacteristic(service).then((_misoCharacteristic) => {
          console.log('got miso characteristic', _misoCharacteristic);
          setMisoCharacteristic(_misoCharacteristic);
        });
        getMosiCharacteristic(service).then((_mosiCharacteristic) => {
          console.log('got mosi characteristic', _mosiCharacteristic);
          setMosiCharacteristic(_mosiCharacteristic);
        });
        getPumpCharacteristic(service).then((_pumpCharacteristic) => {
          console.log('got pump characteristic', _pumpCharacteristic);
          setPumpCharacteristic(_pumpCharacteristic);
        });
        getBuzzerCharacteristic(service).then((_buzzerCharacteristic) => {
          console.log('got buzzer characteristic', _buzzerCharacteristic);
          setBuzzerCharacteristic(_buzzerCharacteristic);
        });
      })
      .catch((e: any) => {
        console.log('error subscribing device', e);
      });
  }, [csmBleDevice]);

  useEffect(() => {
    const onBeforeUnload = async (event: any) => {
      if (csmBleDevice !== undefined && csmBleDevice.gatt !== undefined && csmBleDevice.gatt.connected) {
        console.log('on beforeunload: disconnecting csm ble device');
        csmBleDevice.gatt.disconnect();
      }
      let confirmationValue = 'Quitting the page will disconnect your device from Neose BLE. Proceed?';
      event.returnValue = confirmationValue;
      return confirmationValue;
    };
    console.log('adding event listener for beforeunload');
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      console.log('removing event listener for beforeunload');
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (!navigatorSupportsWebBle()) {
      return;
    }
    var mutex = true;
    let i = setInterval(async () => {
      if (!mutex) {
        console.debug('refkit port detection: port detection mutex is false - skipping');
        return;
      }
      if (csmBleDevice !== undefined && csmBleDevice.gatt !== undefined && csmBleDevice.gatt.connected) {
        return;
      }
      console.debug('setting csm ble detection mutex to false');
      mutex = false;
      setCSMBleDevice(undefined);
      setCSMIsConnected(false);
      setCSMSerialNumber('');
      try {
        let device: BluetoothDevice | null = null;
        let deviceCandidates = await getBleDeviceCandidates();
        console.log('got ble devices', deviceCandidates);
        for (let _device of deviceCandidates) {
          try {
            let _service = await getBleService(_device);
            console.log('got ble service', _service);
            device = _device;
            break;
          } catch (e: any) {
            console.log('error getting ble service', e.message);
          }
        }
        if (device === null) {
          console.debug('no csm ble device gotten nor found');
          mutex = true;
          return;
        }
        // forget all the other devices
        for (let _device of deviceCandidates) {
          if (_device.id !== device.id) {
            console.log('forgetting unused device', _device);
            void (await _device.forget());
          }
        }
        setCSMBleDevice(device);
        setCSMIsConnected(true);
        console.debug('csm ble device found', device);
      } catch (e: any) {
        console.debug('no csm ble device found', e.message);
      }
      console.debug('setting csm ble detection mutex to true');
      mutex = true;
      return;
    }, DEFAULT_DEVICE_DETECTION_INTERVAL);
    return () => {
      console.debug('clearing interval for csm ble device detection');
      clearInterval(i);
      console.debug('setting csm ble device detection mutex to true');
      mutex = true;
      return;
    };
  }, [csmBleDevice]);

  return <ConnectionIndicator name="NeOse over BLE" connectPath="/connect/ble" isConnected={csmIsConnected} minimized={minimized} queueLength={csmMessages.length} state={csmState} />;
};
